"use client"

import { useState, useEffect, useMemo } from "react"
import Card from "../../components/inventory/modules/Card/Card"
import AddInventory from "../../components/inventory/add-inventory"
import OrderInventory from "../../components/inventory/order"
import MoveDeviceForm from "../../components/inventory/modal/MoveDeviceForm"
import DeviceDetailsModal from "../../components/inventory/modal/DeviceDetailsModal"
import Modal from "../../components/inventory/modules/Modal/Moadal"
import Button from "../../components/inventory/modules/Button/Button"
import InventorySection from "../../components/inventory/InventorySection"
import SuppliesSection from "../../components/inventory/SuppliesSection"
import { getWarehouseStatuses, getLocationList } from "../../services/inventory/detail"
import styles from "./InventoryDashboardPage.module.css"
import OrderSupplies from "../../components/inventory/modal/OrderSupplies"
import AddSupplies from "../../components/inventory/modal/AddSupplies"

const SupplyManagementPage = () => {
  const [apiLoading, setApiLoading] = useState(false)
  const [statusFilterOptions, setStatusFilterOptions] = useState([])
  const [tableStatusOptions, setTableStatusOptions] = useState([])
  const [activeTab, setActiveTab] = useState("inventory")
  const [locations, setLocations] = useState([])
  const [isAdmin, setIsAdmin] = useState(false)
  const [message, setMessage] = useState("")
  const [messageType, setMessageType] = useState("")
  const [confirmationLoading, setConfirmationLoading] = useState(false)

  const [suppliesRefreshTrigger, setSuppliesRefreshTrigger] = useState(0)
  const [inventoryRefreshTrigger, setInventoryRefreshTrigger] = useState(0)

  // IMPORTANT: location option value now uses location.id (numeric id) instead of location.name
  const locationOptions = useMemo(() => {
    const baseOptions = [{ value: "all", label: "All Locations" }]

    if (locations && locations.length > 0) {
      const dynamicOptions = locations.map((location) => ({
        value: location.id, // pass id (number) to Select so backend receives location_id
        label: location.name || location.state || String(location.id),
        id: location.id,
      }))
      return [...baseOptions, ...dynamicOptions]
    }

    return baseOptions
  }, [locations])

  const [deviceDetailsModal, setDeviceDetailsModal] = useState({
    isOpen: false,
    selectedDevice: null,
    editMode: false,
    isIndividualDevice: false,
  })
  const [addInventoryModal, setAddInventoryModal] = useState({ isOpen: false })
  const [orderInventoryModal, setOrderInventoryModal] = useState({ isOpen: false })
  const [editSupplyModal, setEditSupplyModal] = useState({
    isOpen: false,
    selectedSupply: null,
  })
  const [moveSupplyModal, setMoveSupplyModal] = useState({
    isOpen: false,
    selectedSupplies: [],
    isBulkMove: false,
  })
  const [moveDeviceModal, setMoveDeviceModal] = useState({
    isOpen: false,
    selectedDevices: [],
    isBulkMove: false,
  })

  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: null,
    confirmText: "Delete",
    variant: "danger",
  })

  const [addSuppliesModal, setAddSuppliesModal] = useState({ isOpen: false })
  const [orderSuppliesModal, setOrderSuppliesModal] = useState({ isOpen: false })

  const handleInventoryUpdate = () => {
    // Trigger inventory section refresh by incrementing the trigger
    setInventoryRefreshTrigger((prev) => prev + 1)
  }

  const handleSupplyUpdate = () => {
    // Trigger supplies section refresh by incrementing the trigger
    setSuppliesRefreshTrigger((prev) => prev + 1)
  }

  const buildStatusFilterOptions = (warehouseStatuses) => {
    const { inventory_statuses = [] } = warehouseStatuses || {}

    const pureOptions = inventory_statuses.map((status) => ({
      value: status.toLowerCase(),
      label: status,
      color: "#dbeafe",
      text: "#1e40af",
      dotColor: "#1e40af",
    }))

    const withAll = [
      { value: "all", label: "All Status", color: "#dbeafe", text: "#1e40af", dotColor: "#1e40af" },
      ...pureOptions,
    ]

    return { pureOptions, withAll }
  }

  const handleConfirmationAction = async (onConfirm) => {
    if (!onConfirm) return

    setConfirmationLoading(true)
    try {
      await onConfirm()
    } finally {
      setConfirmationLoading(false)
    }
  }

  useEffect(() => {

    const checkAdminRole = () => {
      try {
        const inventoryActiveAccount = localStorage.getItem("inventory_active_account")

        if (inventoryActiveAccount) {
          const account = JSON.parse(inventoryActiveAccount)
          const isAdmin = account.type === "admin" || account.type === ""
          setIsAdmin(isAdmin)
          return isAdmin
        } else {
          //  Agar account hi nahi mila to by default admin
          setIsAdmin(true)
          return true
        }
      } catch (error) {
        // Agar parse error ya koi aur error aaya to bhi admin consider karo
        setIsAdmin(true)
        return true
      }
    }

    const initializeData = async () => {
      try {
        const isUserAdmin = checkAdminRole()

        const statusesResponse = await getWarehouseStatuses()
        const { pureOptions, withAll } = buildStatusFilterOptions(statusesResponse)
        setStatusFilterOptions(withAll)
        setTableStatusOptions(pureOptions)

        if (!isUserAdmin) {
          const locationResponse = await getLocationList()
          if (locationResponse.success) {
            setLocations(locationResponse.data)
          }
        }
      } catch (error) {
        // console.error(" Error during initial data fetch:", error)
      }
    }

    initializeData()
  }, [])

  // header buttons should only be visible to admins
  const inventoryHeaderButtons = isAdmin
    ? [
        {
          label: "Add Devices",
          variant: "secondary",
          onClick: () => setAddInventoryModal({ isOpen: true }),
        },
        {
          label: "Order Devices",
          variant: "primary",
          onClick: () => setOrderInventoryModal({ isOpen: true }),
        },
      ]
    : []

  const suppliesHeaderButtons = isAdmin
    ? [
        {
          label: "Add Supplies",
          variant: "secondary",
          onClick: () => setAddSuppliesModal({ isOpen: true }),
        },
        {
          label: "Order Supplies",
          variant: "primary",
          onClick: () => setOrderSuppliesModal({ isOpen: true }),
        },
      ]
    : []

  return (
    <div className={styles.supply_management_page}>
      <div className={styles.page_header}>
        <div className={styles.header_left}>
          <h1 className={styles.page_title}>Inventory</h1>
          <p className={styles.page_subtitle}>Manage your inventory of devices across all warehouses</p>
        </div>
      </div>

      <div className={styles.section}>
        <Card
          title="Inventory Details"
          className={styles.inventory_card}
          headerButtons={inventoryHeaderButtons}
        >
          <InventorySection
            statusFilterOptions={statusFilterOptions}
            locationOptions={locationOptions}
            isAdmin={isAdmin}
            onMoveDeviceModal={setMoveDeviceModal}
            onDeviceDetailsModal={setDeviceDetailsModal}
            onConfirmationModal={setConfirmationModal}
            onInventoryUpdate={handleInventoryUpdate}
            refreshTrigger={inventoryRefreshTrigger}
          />
        </Card>
      </div>

      <div className={styles.section}>
        <Card
          title="Supplies"
          className={styles.supplies_card}
          headerButtons={suppliesHeaderButtons}
        >
          <SuppliesSection
            locationOptions={locationOptions}
            isAdmin={isAdmin}
            onEditSupplyModal={setEditSupplyModal}
            onMoveSupplyModal={setMoveSupplyModal}
            refreshTrigger={suppliesRefreshTrigger}
          />
        </Card>
      </div>

      <DeviceDetailsModal
        device={deviceDetailsModal.selectedDevice}
        isOpen={deviceDetailsModal.isOpen}
        onClose={() => setDeviceDetailsModal({ isOpen: false, selectedDevice: null, editMode: false })}
        editMode={deviceDetailsModal.editMode}
        isIndividualDevice={deviceDetailsModal.isIndividualDevice}
        onDataRefresh={handleInventoryUpdate}
      />

      <MoveDeviceForm
        devices={moveDeviceModal.selectedDevices}
        isBulkMove={moveDeviceModal.isBulkMove}
        isOpen={moveDeviceModal.isOpen}
        onClose={() => setMoveDeviceModal({ isOpen: false, selectedDevices: [], isBulkMove: false })}
      />

      {addInventoryModal.isOpen && (
        <AddInventory
          isOpen={addInventoryModal.isOpen}
          setIsOpen={(isOpen) => setAddInventoryModal({ isOpen })}
          onInventoryUpdate={handleInventoryUpdate}
        />
      )}

      {orderInventoryModal.isOpen && (
        <OrderInventory
          isOpen={orderInventoryModal.isOpen}
          setIsOpen={(isOpen) => setOrderInventoryModal({ isOpen: isOpen })}
        />
      )}

      {addSuppliesModal.isOpen && (
        <AddSupplies
          isOpen={addSuppliesModal.isOpen}
          setIsOpen={(isOpen) => setAddSuppliesModal({ isOpen })}
          onSupplyUpdate={handleSupplyUpdate}
        />
      )}

      {orderSuppliesModal.isOpen && (
        <OrderSupplies
          isOpen={orderSuppliesModal.isOpen}
          setIsOpen={(isOpen) => setOrderSuppliesModal({ isOpen })}
          onOrderUpdate={handleSupplyUpdate}
        />
      )}

      <Modal
        isOpen={confirmationModal.isOpen}
        onClose={() => setConfirmationModal({ isOpen: false, title: "", message: "", onConfirm: null })}
        title={confirmationModal.title}
        width="500px"
        height="auto"
        footer={
          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
            <Button
              variant="secondary"
              onClick={() => setConfirmationModal({ isOpen: false, title: "", message: "", onConfirm: null })}
            >
              Cancel
            </Button>
            <Button
              variant={confirmationModal.variant}
              onClick={() => handleConfirmationAction(confirmationModal.onConfirm)}
              loading={confirmationLoading}
            >
              {confirmationModal.confirmText}
            </Button>
          </div>
        }
      >
        <p style={{ margin: 0, lineHeight: "1.5" }}>{confirmationModal.message}</p>
      </Modal>
    </div>
  )
}

export default SupplyManagementPage
